import { AlertTriangle, RefreshCw } from "lucide-react";
import { useBackendHealth } from "@/contexts/BackendHealthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BackendStatusBannerProps {
  className?: string;
}

/**
 * Shows a persistent banner when the backend is offline
 */
export function BackendStatusBanner({ className }: BackendStatusBannerProps) {
  const { status, checkNow, lastError } = useBackendHealth();

  // Never show on /auth — must not block login UI
  if (status !== "offline") return null;
  if (window.location.pathname.startsWith("/auth")) return null;

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 z-[100] bg-destructive/10 text-destructive-foreground border-b border-destructive/30",
        "flex items-center justify-center gap-3 py-2 px-4 text-sm font-medium",
        "safe-area-top animate-fade-in",
        className
      )}
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0 text-destructive" />
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
        <span className="font-semibold text-destructive">Reconnecting…</span>
        <span className="text-muted-foreground text-xs sm:text-sm">
          Your work is saved and will sync automatically.
        </span>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={checkNow}
        className="ml-2 h-7 px-2"
      >
        <RefreshCw className="h-3 w-3 mr-1" />
        Retry
      </Button>
    </div>
  );
}
